import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import { Common, PopUpBox } from '../../components';
import { styles, options, numberWithNoCommas, numberWithCommas } from '../../utils/common';

import { actionGetConfig, actionSubmitForm21 } from '../../store/application/actions';
import { actionTracking } from '../../store/common/actions';
import DropdownIndicator from '../../components/Common/DropdownIndicator';
import ConfirmByPassForm23 from '../../components/PopUpBox/ConfirmByPassForm23';
import AddressForm from '../../components/PopUpBox/AddressForm';
import { actionLendelaAppInfo } from '../../store/lendela/action';
import {
  buildConsentRouteState,
  buildForm21RouteState,
  clearApplicationDraft,
  createApplicationFlowMeta,
  getApplicationDraft,
  saveApplicationDraft,
} from '../../utils/applicationDraft';
import { APPLICATION_FLOW_STEP, buildApplicationFlowPreviousRoute, canMoveApplicationFlowHistoryBack, getApplicationFlowHistoryIndex, isApplicationFlowBackPopState } from '../../utils/applicationFlowBackNavigation';

function ApplicationForm21() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const applicationDraft = getApplicationDraft();
  const flowMeta = createApplicationFlowMeta({
    phone: location.state?.form1?.phone_number || applicationDraft?.meta?.phone,
    lendelaAppId: applicationDraft?.meta?.lendelaAppId,
    noDocLoanData: applicationDraft?.meta?.noDocLoanData,
  });
  const matchedDraft = flowMeta?.phone ? getApplicationDraft(flowMeta) : applicationDraft;
  const getLatestMatchedDraft = () => {
    if (!flowMeta?.phone) {
      return applicationDraft;
    }

    return getApplicationDraft(flowMeta) ?? applicationDraft;
  };
  const restoredState = !location.state?.form1 ? buildForm21RouteState(matchedDraft) : null;
  const pageState = location.state ?? restoredState;
  const {
    setValue,
    register,
    handleSubmit,
    getValues,
    setError,
    formState: { errors, isSubmitting },
  } = useForm();
  const [mpf, setMpf] = useState(null);
  const [isVisibleInvalidToken, setIsVisibleInvalidToken] = useState(false);
  const [confirmByPass, setConfirmByPass] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);

  const [lendelaData, setLendelaData] = useState(null);
  const [jobTypeDefault, setJobTypeDefault] = useState(null);
  const [businessTypeDefault, setBusinessTypeDefault] = useState(null);
  const [positionDefault, setPositionDefault] = useState(null);
  const [employmentTypeDefault, setEmploymentTypeDefault] = useState(null);
  const [receiptWayDefault, setReceiptWayDefault] = useState(null);
  const [monthlyIncomeDefault, setMonthlyIncomeDefault] = useState(null);
  const [payrollMethodDefault, setPayrollMethodDefault] = useState(null);
  const [mpfDefault, setMpfDefault] = useState(null);
  const [enableEkycButton, setEnableEkycButton] = useState(null);
  const [loading, setLoading] = useState(false);
  const skipPersistOnUnmountRef = useRef(false);
  const persistOnUnmountRef = useRef(null);

  useEffect(() => {
    dispatch(
      actionTracking({
        url: window.location.href,
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    getConfigEkycButton();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!location.state?.form1) {
      if (restoredState?.form1) {
        navigate('/application-form-2-1', {
          replace: true,
          state: restoredState,
        });
        return;
      }

      if (flowMeta?.phone) {
        navigate('/application-form-1', {
          replace: true,
          state: buildConsentRouteState(flowMeta),
        });
        return;
      }

      navigate('/');
      return;
    }

    const latestDraft = getLatestMatchedDraft();

    saveApplicationDraft({
      meta: flowMeta,
      currentStep: 'form21',
      form1: latestDraft?.form1 ?? pageState?.form1,
      form21: latestDraft?.form21,
      form22: latestDraft?.form22,
    });
  }, [flowMeta?.lendelaAppId, flowMeta?.phone, location.state?.form1, navigate, pageState?.form1?.phone_number, restoredState?.form1]);

  useEffect(() => {
    const latestDraft = getLatestMatchedDraft();

    if (latestDraft?.form21) {
      hydrateForm21Draft(latestDraft.form21);
    }
  }, [matchedDraft?.updatedAt]);

  useEffect(() => {
    if (pageState?.lendela_data) {
      setLendelaData(pageState.lendela_data);
      fillLendelaData(pageState.lendela_data);
      return;
    }

    if (!flowMeta?.lendelaAppId) {
      return;
    }

    async function getLendelaInfo() {
      const response = await dispatch(actionLendelaAppInfo(flowMeta.lendelaAppId));
      if (response?.sts_code === 0) {
        const data = response.content;
        setLendelaData(data);
        fillLendelaData(data);
      }
    }

    getLendelaInfo();
  }, [dispatch, flowMeta?.lendelaAppId, pageState?.lendela_data]);

  const fillLendelaData = (data) => {
    if (data?.monthlyIncome) {
      const monthlyIncome = numberWithCommas(data.monthlyIncome);
      setMonthlyIncomeDefault(monthlyIncome);
      setValue('monthly_income', monthlyIncome);
    }
    if (data?.jobIndustry) {
      const businessType = options.business_type.find((item) => item.lendela_value.includes(data.jobIndustry));
      if (businessType) {
        setValue('business_type', businessType.value);
        setBusinessTypeDefault(businessType);
      }
    }
    if (data?.payrollType) {
      const receiptWay = options.receipt_way.find((item) => item.lendela_value.includes(data.payrollType));
      if (receiptWay) {
        setValue('receipt_way', receiptWay.value);
        setReceiptWayDefault(receiptWay);
      }
    }
  };

  const hydrateForm21Draft = (data) => {
    if (!data) {
      return;
    }

    if (data.job_type) {
      setValue('job_type', data.job_type);
      setJobTypeDefault(options.job_type.find((item) => item.value === data.job_type) ?? null);
    }

    if (data.business_type) {
      setValue('business_type', data.business_type);
      setBusinessTypeDefault(options.business_type.find((item) => item.value === data.business_type) ?? null);
    }

    if (data.position) {
      setValue('position', data.position);
      setPositionDefault(options.position.find((item) => item.value === data.position) ?? null);
    }

    if (data.employment_type) {
      setValue('employment_type', data.employment_type);
      setEmploymentTypeDefault(options.employment_type.find((item) => item.value === data.employment_type) ?? null);
    }

    if (data.receipt_way) {
      setValue('receipt_way', data.receipt_way);
      setReceiptWayDefault(options.receipt_way.find((item) => item.value === data.receipt_way) ?? null);
    }

    if (data.monthly_income !== undefined && data.monthly_income !== null && data.monthly_income !== '') {
      const monthlyIncome = numberWithCommas(data.monthly_income);
      setMonthlyIncomeDefault(monthlyIncome);
      setValue('monthly_income', monthlyIncome);
    }

    if (data.payroll_method) {
      setValue('payroll_method', data.payroll_method);
      setPayrollMethodDefault(options.payroll_method.find((item) => item.value === data.payroll_method) ?? null);
    }

    if (data.mpf) {
      setValue('mpf', data.mpf);
      setMpf(data.mpf);
      setMpfDefault(options.mpf.find((item) => item.value === data.mpf) ?? null);
    }
  };

  const buildForm21DraftSnapshot = (baseData = {}) => {
    const currentValues = getValues();

    return {
      ...baseData,
      business_type: currentValues?.business_type ?? baseData?.business_type,
      job_type: currentValues?.job_type ?? baseData?.job_type,
      position: currentValues?.position ?? baseData?.position,
      employment_type: currentValues?.employment_type ?? baseData?.employment_type,
      receipt_way: currentValues?.receipt_way ?? baseData?.receipt_way,
      monthly_income: currentValues?.monthly_income ? numberWithNoCommas(currentValues.monthly_income) : (baseData?.monthly_income ?? ''),
      payroll_method: currentValues?.payroll_method ?? baseData?.payroll_method,
      mpf: currentValues?.mpf ?? baseData?.mpf ?? mpf,
    };
  };

  const persistCurrentForm21Draft = (currentStep = 'form21') => {
    if (!flowMeta?.phone) {
      return null;
    }

    const latestDraft = getLatestMatchedDraft();
    const snapshot = buildForm21DraftSnapshot(latestDraft?.form21 ?? {});

    saveApplicationDraft({
      meta: flowMeta,
      currentStep,
      form1: latestDraft?.form1 ?? pageState?.form1,
      form21: snapshot,
      form22: latestDraft?.form22,
    });

    return snapshot;
  };

  persistOnUnmountRef.current = () => persistCurrentForm21Draft('form21');

  const navigateToPreviousStep = ({ replace = false, useBrowserHistory = false } = {}) => {
    const snapshot = persistCurrentForm21Draft('form1');
    const previousRoute = buildApplicationFlowPreviousRoute({
      step: APPLICATION_FLOW_STEP.FORM21,
      flowMeta,
      draft: snapshot ?? getLatestMatchedDraft(),
    });

    if (!previousRoute) {
      return;
    }

    skipPersistOnUnmountRef.current = true;

    if (useBrowserHistory && canMoveApplicationFlowHistoryBack()) {
      navigate(-1);
      return;
    }

    navigate(previousRoute.pathname, {
      replace,
      state: previousRoute.state,
    });
  };

  const onSubmit = async (data) => {
    const obj = {
      business_type: data?.business_type,
      job_type: data?.job_type,
      position: data?.position,
      employment_type: data?.employment_type,
      receipt_way: data?.receipt_way,
      monthly_income: numberWithNoCommas(data?.monthly_income),
      payroll_method: data?.payroll_method,
      mpf,
    };
    const response = await dispatch(actionSubmitForm21(obj));
    if (response?.sts_code === 0) {
      const latestDraft = getLatestMatchedDraft();
      skipPersistOnUnmountRef.current = true;

      saveApplicationDraft({
        meta: flowMeta,
        currentStep: 'form22',
        form1: latestDraft?.form1 ?? pageState?.form1,
        form21: obj,
        form22: latestDraft?.form22,
      });
      navigate('/application-form-2-2', {
        state: {
          status: true,
          form1: pageState?.form1,
          form2: obj,
          lendela_data: lendelaData,
        },
      });
    } else if (response?.sts_code === 3) {
      clearApplicationDraft();
      localStorage.removeItem('access_token');
      localStorage.removeItem('application_phone');
      setIsVisibleInvalidToken(true);
    }
  };

  useEffect(() => {
    const beforeunload = (e) => {
      persistCurrentForm21Draft('form21');
      const event = e || window.event;
      if (event) {
        event.returnValue = 'Sure?';
      }
      return 'Sure?';
    };

    let currentHistoryIndex = getApplicationFlowHistoryIndex();

    const handlePopState = (event) => {
      const shouldMoveToPreviousStep = isApplicationFlowBackPopState(event, currentHistoryIndex);
      currentHistoryIndex = getApplicationFlowHistoryIndex();

      if (shouldMoveToPreviousStep) {
        navigateToPreviousStep({ replace: true });
        return;
      }

      persistCurrentForm21Draft('form21');
    };

    window.addEventListener('beforeunload', beforeunload);
    window.addEventListener('popstate', handlePopState, true);

    return () => {
      window.removeEventListener('beforeunload', beforeunload);
      window.removeEventListener('popstate', handlePopState, true);
    };
  }, [flowMeta, matchedDraft?.form1, matchedDraft?.form21, matchedDraft?.form22, mpf, navigateToPreviousStep, pageState?.form1]);

  useEffect(() => {
    return () => {
      if (!skipPersistOnUnmountRef.current && persistOnUnmountRef.current) {
        persistOnUnmountRef.current();
      }
    };
  }, []);

  const onConfirmByPass = (status) => {
    setConfirmByPass(false);
    if (status) {
      setShowAddressForm(true);
    }
  };

  const onClickPrevious = () => {
    navigateToPreviousStep({ useBrowserHistory: true });
  };

  const addressFormSubmit = (data) => {
    console.log('address form: ', data);
  };

  const getConfigEkycButton = async () => {
    setLoading(true);
    const response = await dispatch(actionGetConfig('ENABLE_EKYC_BUTTON'));
    if (response?.sts_code === 0) {
      setEnableEkycButton(response?.content === '1');
    }
    setLoading(false);
  };

  if (!location.state?.form1 && restoredState?.form1) {
    return null;
  }

  return (
    <>
      {showAddressForm && <AddressForm lendelaData={lendelaData} form1={pageState?.form1} onSubmit={addressFormSubmit}></AddressForm>}
      <form onSubmit={handleSubmit(onSubmit)}>
        {isVisibleInvalidToken && <PopUpBox.InvalidToken />}
        <Common.Loading isVisible={isSubmitting || loading} />
        {confirmByPass && enableEkycButton !== null && <ConfirmByPassForm23 enableEkycButton={enableEkycButton} onConfirm={onConfirmByPass}></ConfirmByPassForm23>}
        <div className="bg-white">
          <div className="p-5">
            <Common.StepBar active={1} />
          </div>
          <p className="flex-1 text-2xl text-white bg-primary pl-5 py-2 rounded-tr-full rounded-br-full w-36 font-semibold mb-4">職業資料</p>
          <div className="p-5">
            <div className="mb-6">
              <p className="text-xl text-gray font-bold mb-2">工作類型</p>
              <div className="border-b border-primary relative">
                <Select
                  placeholder="- 請選擇 -"
                  className="w-full"
                  theme={(theme) => ({
                    ...theme,
                    borderRadius: 0,
                  })}
                  styles={styles}
                  {...register('job_type', { required: true })}
                  onChange={(e) => {
                    setValue('job_type', e.value);
                    setError('job_type', null);
                    setJobTypeDefault(e);
                  }}
                  options={options.job_type}
                  isSearchable={false}
                  components={{ DropdownIndicator }}
                  value={jobTypeDefault}
                />
              </div>
              {errors?.job_type && errors?.job_type?.type === 'required' && <span className="text-10 text-red-500">*必填</span>}
            </div>
            <div className="mb-6">
              <p className="text-xl text-gray font-bold mb-2">業務類型</p>
              <div className="border-b border-primary relative">
                <Select
                  placeholder="- 請選擇 -"
                  className="w-full"
                  theme={(theme) => ({
                    ...theme,
                    borderRadius: 0,
                  })}
                  styles={styles}
                  {...register('business_type', { required: true })}
                  onChange={(e) => {
                    setValue('business_type', e.value);
                    setError('business_type', null);
                    setBusinessTypeDefault(e);
                  }}
                  options={options.business_type}
                  isSearchable={false}
                  components={{ DropdownIndicator }}
                  value={businessTypeDefault}
                  isDisabled={!!businessTypeDefault && !!lendelaData}
                />
              </div>
              {errors?.business_type && errors?.business_type?.type === 'required' && <span className="text-10 text-red-500">*必填</span>}
            </div>
            <div className="mb-6">
              <p className="text-xl text-gray font-bold mb-2">職位</p>
              <div className="border-b border-primary relative">
                <Select
                  placeholder="- 請選擇 -"
                  className="w-full"
                  theme={(theme) => ({
                    ...theme,
                    borderRadius: 0,
                  })}
                  styles={styles}
                  {...register('position', { required: true })}
                  onChange={(e) => {
                    setValue('position', e.value);
                    setError('position', null);
                    setPositionDefault(e);
                  }}
                  options={options.position}
                  isSearchable={false}
                  components={{ DropdownIndicator }}
                  value={positionDefault}
                />
              </div>
              {errors?.position && errors?.position?.type === 'required' && <span className="text-10 text-red-500">*必填</span>}
            </div>
            <div className="mb-6">
              <p className="text-xl text-gray font-bold mb-2">受聘形式</p>
              <div className="border-b border-primary relative">
                <Select
                  placeholder="- 請選擇 -"
                  className="w-full"
                  theme={(theme) => ({
                    ...theme,
                    borderRadius: 0,
                  })}
                  styles={styles}
                  {...register('employment_type', { required: true })}
                  onChange={(e) => {
                    setValue('employment_type', e.value);
                    setError('employment_type', null);
                    setEmploymentTypeDefault(e);
                  }}
                  options={options.employment_type}
                  isSearchable={false}
                  components={{ DropdownIndicator }}
                  value={employmentTypeDefault}
                />
              </div>
              {errors?.employment_type && errors?.employment_type?.type === 'required' && <span className="text-10 text-red-500">*必填</span>}
            </div>
            <div className="mb-6">
              <p className="text-xl text-gray font-bold mb-2">出糧方式</p>
              <div className="border-b border-primary relative">
                <Select
                  placeholder="- 請選擇 -"
                  className="w-full"
                  theme={(theme) => ({
                    ...theme,
                    borderRadius: 0,
                  })}
                  styles={styles}
                  {...register('receipt_way', { required: true })}
                  onChange={(e) => {
                    setValue('receipt_way', e.value);
                    setError('receipt_way', null);
                    setReceiptWayDefault(e);
                  }}
                  options={options.receipt_way}
                  isSearchable={false}
                  components={{ DropdownIndicator }}
                  value={receiptWayDefault}
                  isDisabled={!!receiptWayDefault && !!lendelaData}
                />
              </div>
              {errors?.receipt_way && errors?.receipt_way?.type === 'required' && <span className="text-10 text-red-500">*必填</span>}
            </div>
            <div className="mb-6">
              <p className="text-xl text-gray font-bold mb-2">月收入</p>
              <Common.InputMoney defaultValue={monthlyIncomeDefault} disabled={!!lendelaData?.monthlyIncome} setValue={setValue} zeroAllow={true} placeholder=" " register={register} title="monthly_income" setError={setError} errors={errors} />
            </div>
            <div className="mb-6">
              <p className="text-xl text-gray font-bold mb-2">支薪方式</p>
              <div className="border-b border-primary relative">
                <Select
                  placeholder="- 請選擇 -"
                  className="w-full"
                  theme={(theme) => ({
                    ...theme,
                    borderRadius: 0,
                  })}
                  styles={styles}
                  {...register('payroll_method', { required: true })}
                  onChange={(e) => {
                    setValue('payroll_method', e.value);
                    setError('payroll_method', null);
                    setPayrollMethodDefault(e);
                  }}
                  options={options.payroll_method}
                  isSearchable={false}
                  components={{ DropdownIndicator }}
                  value={payrollMethodDefault}
                />
              </div>
              {errors?.payroll_method && errors?.payroll_method?.type === 'required' && <span className="text-10 text-red-500">*必填</span>}
            </div>
            <div className="mb-6">
              <p className="text-xl text-gray font-bold mb-2">MPF強積金</p>
              <div className="border-b border-primary relative">
                <Select
                  placeholder="- 請選擇 -"
                  className="w-full"
                  theme={(theme) => ({
                    ...theme,
                    borderRadius: 0,
                  })}
                  styles={styles}
                  {...register('mpf', { required: true })}
                  onChange={(e) => {
                    setValue('mpf', e.value);
                    setMpf(e.value);
                    setError('mpf', null);
                    setMpfDefault(e);
                  }}
                  options={options.mpf}
                  isSearchable={false}
                  components={{ DropdownIndicator }}
                  value={mpfDefault}
                />
              </div>
              {errors?.mpf && errors?.mpf?.type === 'required' && <span className="text-10 text-red-500">*必填</span>}
            </div>
          </div>
          <Common.FormActionRow
            onBack={onClickPrevious}
            nextLabel="下一步"
            disabled={isSubmitting}
            loading={isSubmitting}
          />
        </div>
      </form>
    </>
  );
}

export default ApplicationForm21;
